import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLButtonElement} [viewMoreButton] - The view more button element.
 * @property {HTMLElement} [viewMoreContainer] - The view more button wrapper container.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.refs.viewMoreButton) {
      this.refs.viewMoreButton.addEventListener('click', () => {
        this.classList.add('media-gallery--expanded');
        
        // Hide the view more button container completely so they can't collapse it
        if (this.refs.viewMoreContainer) {
          this.refs.viewMoreContainer.style.display = 'none';
        } else {
          this.refs.viewMoreButton.style.display = 'none';
        }
      }, { signal });

      // Preload hidden images after page load to ensure smooth expansion
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => this.#preloadHiddenImages());
      } else {
        setTimeout(() => this.#preloadHiddenImages(), 2000);
      }
    }
  }

  #preloadHiddenImages() {
    const hiddenImages = this.querySelectorAll('.media-gallery__grid li:nth-child(n+7) img');
    hiddenImages.forEach((img) => {
      const src = img.getAttribute('src');
      const srcset = img.getAttribute('srcset');
      if (src || srcset) {
        const tempImg = new Image();
        if (srcset) tempImg.srcset = srcset;
        if (src) tempImg.src = src;
      }
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
